import { kObject, kObjectArray } from "../ReUsableTS/kBrowserObjects";
import ks from "../ReUsableTS/kBrowserShared";
import { kEditableTableActive, kHTMLEditableColType } from "./kEditableTableTypes";
import type { kEditableTable } from "./kEditableTable";
import type { kEditableTableCell } from "./kEditableTableCell";

// kEditableTableRow / kEditableTableRows, moved out of kEditableTable.ts (size split, 2026-10-20); re-exported from kEditableTable.ts.

export class kEditableTableRow extends kObject{
    public ParentTable:kEditableTable|null = null;
    public RowNumberCell:HTMLTableCellElement|null = null;
    // The row number lives in its own span so RowNumberCell.textContent still
    // returns just the number, which the existing suites depend on.
    public RowNumberSpan:HTMLSpanElement|null = null;
    public Row: HTMLTableRowElement |null = null;


    // NOT "filtered out" - Hidden is set only by FadeOut/FadeIn (a row the user deleted). The
    // filter and the pager hide rows through Hide()/Show() (style.display) and never touch it,
    // so an "act on what the user can see" loop must ask IsOnScreen, not read Hidden.
    public Hidden:boolean = false;

    /** True when the row is drawn right now: not deleted, passes the filter, on the current page. */
    public IsOnScreen = (): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (this.Hidden === true) { return false; }
            if (this.Row === null) { return false; }
            return this.Row.style.display !== "none";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRow.IsOnScreen");
            return false;
        }
    }
    constructor(Index: number) {
        super();
        if (ks.KErrorMode === true) { return; }
        try {

            this.Row = document.createElement("tr");
            this.Row.className = "kEditableTableRow";
            this.Row.setAttribute("role", "row");
            this.RowNumberCell = document.createElement("td");
            this.RowNumberCell.className = "kRowNumberHeading";
            this.RowNumberCell.setAttribute("role", "rowheader");
            this.RowNumberCell.addEventListener("click", this.RowClick);
            this.RowNumberCell.addEventListener("contextmenu", this.RowRightClick);
            this.RowNumberSpan = document.createElement("span");
            this.RowNumberSpan.className = "kRowNumText";
            this.RowNumberSpan.textContent = (Index +1).toString();
            this.RowNumberCell.appendChild(this.RowNumberSpan);
            this.Row.appendChild(this.RowNumberCell);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRow.constructor");
            return;
        }
    }

    // Updates the visible row number through its dedicated span. Callers used to
    // assign RowNumberCell.textContent directly, which wiped any child element;
    // routing through the number span keeps the cell structure intact.
    public SetRowNumberText = (Text:string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if(this.RowNumberCell === null){ return; }
            if(this.RowNumberSpan === null || this.RowNumberSpan.parentNode === null){
                this.RowNumberSpan = document.createElement("span");
                this.RowNumberSpan.className = "kRowNumText";
                this.RowNumberCell.insertBefore(this.RowNumberSpan, this.RowNumberCell.firstChild);
            }
            this.RowNumberSpan.textContent = Text;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRow.SetRowNumberText");
            return;
        }
    }

    public RowClick = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if(this.ParentTable){
                kEditableTableActive.SetActiveTable(this.ParentTable);
                this.ParentTable.Selection.SetSingleRowSelected(this);
                if(this.ParentTable.RowClickCB){
                    this.ParentTable.RowClickCB(this);
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRow.RowClick");
            return;
        }
    }
    public RowRightClick = (Event:MouseEvent): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if(this.ParentTable){
                if(this.ParentTable.RowRightClickCB){
                    this.ParentTable.RowRightClickCB(this,Event);
                }else if(this.ParentTable.RowContextMenu){
                    this.ParentTable.RightClickRow = this;
                    this.ParentTable.RowContextMenu.ShowMenu(Event);
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRow.RowRightClick");
            return;
        }
    }

    public Cells: Array<kEditableTableCell> = new Array<kEditableTableCell>();
    public AddCell = (Cell: kEditableTableCell): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Cells.push(Cell);
            if(this.Row){
                this.Row.appendChild(Cell.Cell);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRow.AddCell");
            return;
        }
    }
    public IsDirty = (): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            let CurrCell:kEditableTableCell;
            for(CurrCell of this.Cells){
                if(CurrCell.Dirty === true){
                    return true;
                }
            }
            return false;   
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRow.IsDirty");
            return false;
        }
    }
    public SetClean = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            let CurrCell:kEditableTableCell;
            for(CurrCell of this.Cells){
                CurrCell.SetClean();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRow.SetClean");
        }
    }
    public GetValueFromCell = (PropertyName:string): any => {
        if (ks.KErrorMode === true) { return; }
        try {
            let CurrCell:kEditableTableCell;
            for(CurrCell of this.Cells){
                if(CurrCell.ColDef.KObjectPropertyName === PropertyName){
                    switch (CurrCell.ColDef.Type) {
                        case kHTMLEditableColType.Select:
                            return ks.Nz(CurrCell.CurrentValue);
                        case kHTMLEditableColType.Number:
                            return ks.N0(CurrCell.CurrentValue);
                        case kHTMLEditableColType.Currency:
                            return ks.NumberFromCurrency(CurrCell.CurrentValue);
                        case kHTMLEditableColType.SelectMulti:
                            if(CurrCell.CurrentValue){
                                return CurrCell.CurrentValue.split("; ");
                            }else{
                                return [];
                            }
                        case kHTMLEditableColType.RichHtml:
                            return ks.Nz(CurrCell.CurrentValue);
                        case kHTMLEditableColType.Geo:
                            // Stored as a JSON string of {Title,Lat,Long}; nz keeps it a
                            // string (empty "" when unset) so a string-bound consumer reads
                            // a clean value, never null/[object Object].
                            return ks.Nz(CurrCell.CurrentValue);
                        default:
                            return CurrCell.CurrentValue;
                    }
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRow.GetValueFromCell");
            return;
        }
    }
    public SetValueToCell = (PropertyName:string,value:any,SetDirty?:boolean): any => {
        if (ks.KErrorMode === true) { return; }
        try {
            let CurrCell:kEditableTableCell;
            for(CurrCell of this.Cells){
                if(CurrCell.ColDef.KObjectPropertyName === PropertyName){
                    return CurrCell.SetNewValue(value,SetDirty);
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRow.SetValueToCell");
            return;
        }
    }
    public Remove = (CB:any): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if(this.Row){
                this.Row.remove();
                if(this.ParentTable){
                    this.ParentTable.Rows.RemoveItemByIndex(this.Index);
                }
            }
            if(this.ParentTable){
                if(this.ParentTable.RowDeletedCB){
                    this.ParentTable.RowDeletedCB(this,CB);
                }
                // A row deletion is a mutation of grid contents — same as
                // an in-place cell edit. Fire CellDirtiedCB so consumers
                // that wire it for persistence (write grid -> column /
                // textarea / list, recompute totals, mark form dirty)
                // pick the change up without having to also wire
                // RowDeletedCB. The two seams stay distinct (RowDeletedCB
                // still fires per-row above for callers that need the row
                // reference); this one is the unified
                // "grid-contents-changed" signal. PasteInProgress check
                // mirrors _FireDirtiedCallback so a paste-driven row
                // replace doesn't fire N×CellDirtied during the bulk op.
                if(this.ParentTable.CellDirtiedCB && this.ParentTable.PasteInProgress === false){
                    ks.DoCallBack(this.ParentTable.CellDirtiedCB, null, false);
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRow.Remove");
        }
    }
    /**
     * Shared row show/hide for the kEditableTable rows and the kSPUI2 list view items:
     * set the row's display style when there is a row. The KGlobalErrorHandler label is
     * always the caller's own.
     */
    public static SetRowDisplay(Row: HTMLTableRowElement | null, Display: string, Label: string): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Row) {
                Row.style.display = Display;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRow.SetRowDisplay");
        }
    }

    public Hide = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            kEditableTableRow.SetRowDisplay(this.Row, "none", "kEditableTableRow.Hide");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRow.Hide");
        }
    }
    public Show = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            kEditableTableRow.SetRowDisplay(this.Row, "table-row", "kEditableTableRow.Show");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRow.Show");
        }
    }
    /**
     * Shared fade: scale the row out or in, run the caller's after-callback once the
     * animation window has passed, and record the hidden state. The KGlobalErrorHandler
     * label is always the caller's own.
     */
    private Fade = (Hidden: boolean, AfterCB: any, Label: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Row) {
                let From: string = "scale(0)";
                let To: string = "scale(1)";
                if (Hidden) { From = "scale(1)"; To = "scale(0)"; }
                this.Row.animate([{ transform: From },{ transform: To }],{duration: 250,iterations: 1});
                window.setTimeout(AfterCB,250);
            }
            this.Hidden = Hidden;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRow.Fade");
        }
    }

    public FadeOut = (AfterCB:any): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Fade(true, AfterCB, "kEditableTableRow.FadeOut");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRow.FadeOut");
        }
    }

    public FadeIn = (AfterCB:any): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Fade(false, AfterCB, "kEditableTableRow.FadeIn");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRow.FadeIn");
        }
    }
    public RemoveAfterFadeOut = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if(this.Row){
                this.Row.remove();
            }
            if(this.ParentTable){
                this.ParentTable.Rows.RemoveItemByUniqueID(this.UniqueID);
                this.ParentTable.UpdateRowNumbers();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRow.RemoveAfterFadeOut");
        }
    }
    public IsEmpty = (): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            // A row is empty only when NO cell carries user data. Checking
            // just Cells[0] (the old behaviour) silently dropped rows whose
            // first column was blank but later columns were filled.
            for (const CurrCell of this.Cells) {
                const CurrValue: any = CurrCell.CurrentValue;
                if (CurrValue === "" || CurrValue === undefined || CurrValue === null) { continue; }
                // An unchecked checkbox/radio carries no data (CurrentValue
                // stays null until the first toggle; on-then-off leaves false).
                if (CurrValue === false) { continue; }
                // A cell still holding its column DefaultValue is grid
                // scaffolding, not user data: AddEmptyRows seeds defaulted
                // columns via SetCellOrgValue, so trailing blank rows in
                // such grids would otherwise never count as empty.
                if (CurrCell.ColDef && CurrCell.ColDef.DefaultValue && CurrValue === CurrCell.ColDef.DefaultValue) { continue; }
                return false;
            }
            return true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRow.IsEmpty");
            return false;
        }
    }

    /**
     * Shared first/last-cell selection: select the cell at the caller's index when the
     * row is parented. The KGlobalErrorHandler label is always the caller's own.
     */
    private SelectCellAt = (CellIndex: number, Label: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            let CurrCell:kEditableTableCell;
            if(this.ParentTable){
                CurrCell = this.Cells[CellIndex];
                CurrCell.Select();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRow.SelectCellAt");
        }
    }

    public SelectLastCell = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.SelectCellAt(this.Cells.length - 1, "kEditableTableRow.SelectLastCell");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRow.SelectLastCell");
        }
    }

    public SelectFirstCell = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.SelectCellAt(0, "kEditableTableRow.SelectFirstCell");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRow.SelectFirstCell");
        }
    }
    public ReadOnly: boolean = false;
    public SetReadOnly = (ReadOnly: boolean): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.ReadOnly = ReadOnly;
            if (this.Row) {
                if (ReadOnly) {
                    this.Row.classList.add("kEditableTableRowReadOnly");
                } else {
                    this.Row.classList.remove("kEditableTableRowReadOnly");
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableRow.SetReadOnly");
        }
    }
}

export class kEditableTableRows extends kObjectArray{
    public Items: kEditableTableRow[] = [];
 
}
